import { createUseStyles } from 'react-jss'
import {
  addDisplayName,
  flowMax,
  addProps,
  addHandlers,
  addState,
} from 'ad-hok'
import { FC } from 'react'
import {
  Media,
  MediaContent,
  MediaInput,
  MediaQueueItem,
  MediaState,
  ProductUrl,
} from 'shared/src/api/types'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { typedAs } from 'shared/src/utils/typedAs'
import { addApi, addUser } from 'shared/src/api'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { addEffectOnMount, branchIfTruthy } from 'ad-hok-utils'
import { range } from 'lodash'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Page } from '../Page'
import { TextAreaNew } from '../TextField'
import { Button } from '../Button'
import { ContentPicker } from '../ContentPicker'
import { addToastContext } from '../toastContext'
import {
  addCmsLoadingAndErrorIndicators,
  Loading,
} from '../../utils/addCmsLoadingAndErrorIndicators'
import {
  BODY_BOLD_STYLE,
  NOTES_BOLD_STYLE,
  NOTES_MEDIUM_STYLE,
} from '../styles'
import { ProductField, ProductFieldSkeleton } from './ProductField'
import { Product } from './Product'
import { addProductLinkingContext } from './productLinking'
import { IconButton } from '../IconButton'
import { SearchModal } from './SearchModal'
import { ProductPickerButton } from './ProductPickerButton'
import { ProductQueueModal } from './ProductQueueModal'
import { DeleteButton } from '../DeleteButton'
import { makeCreatePostProductRoute } from '../../utils/routes'
import { DetectedProductsModal } from './DetectedProductsModal'

const MAX_PRODUCTS = 12

const SEARCH_BUTTON_WIDTH = 56
const useStyles = createUseStyles({
  contentCaptionContainer: {
    marginTop: 32,
    display: 'flex',
    height: 102,
    justifyContent: 'space-between',
    gap: 11,
  },
  caption: {
    flexGrow: 1,
  },
  captionWrapper: {
    height: '100%',
  },
  captionField: {
    resize: 'none',
    height: '100%',
  },
  productsLabel: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    maxWidth: 235,
    marginTop: 24,
  },
  productsTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 32,
    marginBottom: 11,
  },
  productsTitle: {
    ...BODY_BOLD_STYLE,
    color: 'rgba(54, 52, 50, 0.96)',
  },
  productsMinimumOrMaximum: {
    ...NOTES_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.56)',
  },
  productPickerButtonsContainer: {
    display: 'flex',
    gap: 8,
  },
  productPickerButton: {
    flexGrow: 1,
  },
  productsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    gap: 8,
  },
  button: {
    marginTop: 34,
  },
  productFieldRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  productSearchButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: SEARCH_BUTTON_WIDTH,
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    borderRadius: 2,
    border: '1px solid rgba(144, 115, 79, 0.24)',
  },
  productSearchButtonSkeleton: {
    width: SEARCH_BUTTON_WIDTH,
  },
  buttonContainer: {
    display: 'flex',
    gap: 8,
  },
})

export const Post: FC = flowMax(
  addDisplayName('Post'),
  addApi,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'productQueue',
    ({ api, userId }) => api.products.queue(userId),
    ['api'],
    () => 'productQueue'
  ),
  addNavigationState(
    'content',
    'onChangeContent',
    typedAs<MediaContent | null>(null)
  ),
  addDataLoadingFromPromise(
    'detectedProducts',
    ({ api, content }) =>
      content ? api.products.detected(content) : Promise.resolve([]),
    ['api', 'content'],
    ({ content }) => `detectedProducts-${content?.type}-${content?.value}`
  ),
  addNavigationState(
    'mediaQueueItem',
    'setMediaQueueItem',
    typedAs<Partial<MediaQueueItem> | null>(null)
  ),
  addOptionalIntPathParam('resourceId'),
  addState('isPreparingInitialData', 'setPreparingInitialData', false),
  addUser,
  addNavigationState(
    'socialMediaPostId',
    'setSocialMediaPostId',
    ({ mediaQueueItem }) =>
      mediaQueueItem?.sourceType === 'socialMediaPost'
        ? mediaQueueItem.id
        : null
  ),
  addNavigationState(
    'caption',
    'onChangeCaption',
    ({ mediaQueueItem }) => mediaQueueItem?.caption ?? null
  ),
  addNavigationState('productIds', 'setProductIds', typedAs<number[]>([])),
  addHandlers({
    addProductId:
      ({ productIds, setProductIds }) =>
      (productId: number) => {
        if (
          productIds.includes(productId) ||
          productIds.length >= MAX_PRODUCTS
        ) {
          return
        }

        setProductIds([...productIds, productId])
      },
  }),
  addNavigationState('tagIds', 'setTagIds', typedAs<number[]>([])),
  addNavigationState('isSearchModalOpen', 'setIsSearchModalOpen', false),
  addNavigationState('searchText', 'setSearchText', ''),
  addNavigationState(
    'isProductQueueModalOpen',
    'setIsProductQueueModalOpen',
    false
  ),
  addNavigationState(
    'isDetectedProductsModalOpen',
    'setIsDetectedProductsModalOpen',
    false
  ),
  addProductLinkingContext,
  addEffectOnMount(
    ({
        linkProductId,
        clearLinkProductId,
        addProductId,
        setIsSearchModalOpen,
        setSearchText,
        setIsProductQueueModalOpen,
        setIsDetectedProductsModalOpen,
      }) =>
      () => {
        if (!linkProductId) return

        addProductId(linkProductId)

        clearLinkProductId()
        setIsSearchModalOpen(false)
        setSearchText('')
        setIsProductQueueModalOpen(false)
        setIsDetectedProductsModalOpen(false)
      }
  ),
  addNavigationState('hasInitialized', 'setInitialized', false),
  addNavigationState('state', 'setState', typedAs<MediaState | null>(null)),
  addHandlers({
    initializeWithMedia:
      ({
        setState,
        setSocialMediaPostId,
        onChangeCaption,
        onChangeContent,
        setProductIds,
        setTagIds,
      }) =>
      (media: Media) => {
        setState(media.state)
        setSocialMediaPostId(media.socialMediaPostId)
        onChangeCaption(media.caption)
        onChangeContent(media.content)
        setProductIds(media.productIds)
        setTagIds(media.tagIds)
      },
    onDeleteProductId:
      ({ productIds, setProductIds }) =>
      (id: number) => {
        setProductIds(productIds.filter((existingId) => existingId !== id))
      },
  }),
  addEffectOnMount(
    ({
        hasInitialized,
        setInitialized,
        mediaQueueItem,
        resourceId,
        setPreparingInitialData,
        onChangeContent,
        initializeWithMedia,
        api,
      }) =>
      () => {
        if (hasInitialized) {
          return
        }

        const prepareInitialData = async () => {
          if (mediaQueueItem) {
            setPreparingInitialData(true)

            if (mediaQueueItem.contentImageId) {
              onChangeContent({
                type: 'image',
                value: mediaQueueItem.contentImageId,
              })
            } else if (mediaQueueItem.contentVideoId) {
              onChangeContent({
                type: 'video',
                value: mediaQueueItem.contentVideoId,
              })
            }

            setPreparingInitialData(false)
            setInitialized(true)
          } else if (resourceId) {
            setPreparingInitialData(true)
            const media = await api.media.get(resourceId)
            initializeWithMedia(media)
            setPreparingInitialData(false)
            setInitialized(true)
          }
        }

        prepareInitialData()
      }
  ),
  branchIfTruthy('isPreparingInitialData', {
    returns: () => <Loading />,
  }),
  addCmsLoadingAndErrorIndicators('productQueue'),
  addCmsLoadingAndErrorIndicators('detectedProducts'),
  addState('isSaving', 'setSaving', false),
  addNavigate,
  addToastContext,
  addTranslationHelpers,
  addOptionalIntPathParam('userId'),
  addHandlers({
    updateContentAndSocialMediaPostId:
      ({ onChangeContent, setSocialMediaPostId }) =>
      (content: MediaContent | null) => {
        onChangeContent(content)
        setSocialMediaPostId(null)
      },
    onNewProductWithUrl:
      ({ navigate, userId }) =>
      (url: string) => {
        navigate(makeCreatePostProductRoute(userId), {
          state: {
            url,
          },
        })
      },
    onSave:
      ({
        api,
        user,
        userId,
        resourceId,
        socialMediaPostId,
        caption,
        content,
        productIds,
        tagIds,
        setSaving,
        navigate,
        showErrorToast,
        showSuccessToast,
        t,
      }) =>
      async (state: MediaState) => {
        setSaving(true)

        try {
          const input: MediaInput = {
            userId: userId ?? user.id,
            state,
            caption,
            content: content!,
            productIds,
            tagIds,
            socialMediaPostId: socialMediaPostId ?? null,
            position: null,
          }

          if (resourceId) {
            await api.media.update(resourceId, input)
          } else {
            await api.media.create(input)
          }

          navigate(userId ? `/users/${userId}/feed` : '/')
          showSuccessToast({
            title: resourceId
              ? t('admin.post.postUpdatedTitle')
              : t('admin.post.postCreatedTitle'),
            details:
              state === 'published'
                ? t('admin.post.postPublishedDescription')
                : t('admin.post.postDraftedDescription'),
          })
        } catch (error) {
          showErrorToast({
            title: resourceId
              ? t('admin.post.errorSaving')
              : t('admin.post.errorCreating'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setSaving(false)
        }
      },
    onDelete:
      ({
        api,
        resourceId,
        setSaving,
        navigate,
        showErrorToast,
        showSuccessToast,
        t,
      }) =>
      async () => {
        setSaving(true)

        try {
          if (resourceId) {
            await api.media.delete(resourceId)
          }

          navigate('/')
          showSuccessToast({
            title: t('admin.post.postDeletedTitle'),
            details: t('admin.post.postDeletedDescription'),
          })
        } catch (error) {
          showErrorToast({
            title: t('admin.post.errorDeleting'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setSaving(false)
        }
      },
    onProductQueueClick:
      ({ setIsProductQueueModalOpen }) =>
      () => {
        setIsProductQueueModalOpen(true)
      },
    onProductUrlChoose:
      ({ addProductId, setIsProductQueueModalOpen, navigate, userId }) =>
      ({ id, url, product }: ProductUrl) => {
        if (product?.id) {
          addProductId(product.id)
          setIsProductQueueModalOpen(false)
        } else {
          navigate(makeCreatePostProductRoute(userId), {
            state: {
              url,
              productUrlId: id,
            },
          })
        }
      },
    onDetectedProductsClick:
      ({ setIsDetectedProductsModalOpen }) =>
      () => {
        setIsDetectedProductsModalOpen(true)
      },
    onDetectedProductChoose:
      ({ addProductId, setIsDetectedProductsModalOpen }) =>
      (productId: number) => {
        addProductId(productId)
        setIsDetectedProductsModalOpen(false)
      },
    onSearchProductChoose:
      ({ addProductId, setIsSearchModalOpen, setSearchText }) =>
      (productId: number) => {
        addProductId(productId)
        setIsSearchModalOpen(false)
        setSearchText('')
      },
    onProductClick:
      ({ navigate, userId }) =>
      (productId: number) =>
      () => {
        navigate(makeCreatePostProductRoute(userId), {
          state: {
            productId,
          },
        })
      },
  }),
  addProps(({ content, isSaving, productIds }) => ({
    saveDisabled: !content || isSaving || productIds.length === 0,
  })),
  addProps(({ productQueue, detectedProducts }) => ({
    showQueueButton: productQueue.length > 0,
    showDetectedProductsButton: detectedProducts.length > 0,
    numDetectedProducts: detectedProducts.flat(1).length,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    resourceId,
    caption,
    onChangeCaption,
    content,
    updateContentAndSocialMediaPostId,
    showQueueButton,
    showDetectedProductsButton,
    productQueue,
    numDetectedProducts,
    detectedProducts,
    onNewProductWithUrl,
    productIds,
    onDeleteProductId,
    state,
    onSave,
    onDelete,
    saveDisabled,
    isSearchModalOpen,
    setIsSearchModalOpen,
    searchText,
    setSearchText,
    isProductQueueModalOpen,
    setIsProductQueueModalOpen,
    onProductQueueClick,
    isDetectedProductsModalOpen,
    setIsDetectedProductsModalOpen,
    onDetectedProductsClick,
    onProductUrlChoose,
    onDetectedProductChoose,
    onSearchProductChoose,
    onProductClick,
    classes,
    t,
  }) => (
    <Page
      leftButton=""
      title={t('admin.post.title')}
      renderExtraBarContent={
        resourceId
          ? () => (
              <DeleteButton title={t('admin.post.delete')} onClick={onDelete} />
            )
          : undefined
      }
    >
      <div className={classes.contentCaptionContainer}>
        <ContentPicker
          value={content}
          onChange={updateContentAndSocialMediaPostId}
        />
        <TextAreaNew
          id="caption"
          label={t('admin.post.fields.caption')}
          value={caption}
          onChange={onChangeCaption}
          noMargin
          className={classes.caption}
          wrapperClassName={classes.captionWrapper}
          inputClassName={classes.captionField}
        />
      </div>
      <p className={classes.productsLabel}>{t('admin.post.productsLabel')}</p>
      <div className={classes.productsTitleContainer}>
        <div className={classes.productsTitle}>
          {t('admin.post.productsTitle')}
        </div>
        <div className={classes.productsMinimumOrMaximum}>
          {productIds.length > 0
            ? t('admin.post.productsMaximum', { max: MAX_PRODUCTS })
            : t('admin.post.productsMinimum')}
        </div>
      </div>
      {(showQueueButton || showDetectedProductsButton) && (
        <div className={classes.productPickerButtonsContainer}>
          {showQueueButton && (
            <ProductPickerButton
              type="queue"
              titleStyle={showDetectedProductsButton ? 'short' : 'long'}
              number={productQueue.length}
              onClick={onProductQueueClick}
              className={classes.productPickerButton}
            />
          )}
          {showDetectedProductsButton && (
            <ProductPickerButton
              type="detected"
              titleStyle={showQueueButton ? 'short' : 'long'}
              number={numDetectedProducts}
              onClick={onDetectedProductsClick}
              className={classes.productPickerButton}
            />
          )}
        </div>
      )}
      <div className={classes.productsContainer}>
        {productIds.map((id) => (
          <Product
            product={{ id }}
            onDelete={() => onDeleteProductId(id)}
            onClick={onProductClick(id)}
            key={id}
          />
        ))}
        {range(MAX_PRODUCTS - productIds.length).map((index) => (
          <div className={classes.productFieldRowContainer} key={index}>
            {index > 0 ? (
              <ProductFieldSkeleton
                className={classes.productSearchButtonSkeleton}
              />
            ) : (
              <IconButton
                name="search"
                onClick={() => setIsSearchModalOpen(true)}
                className={classes.productSearchButton}
              />
            )}
            <ProductField
              onNewWithUrl={onNewProductWithUrl}
              disabled={index > 0}
            />
          </div>
        ))}
      </div>
      <div className={classes.buttonContainer}>
        {state !== 'published' && (
          <Button
            type="fullWidth"
            text={t('admin.post.saveDraft')}
            icon="next"
            showIconAfterText
            onClick={() => onSave('creatorDraft')}
            disabled={saveDisabled}
            className={classes.button}
          />
        )}
        <Button
          type="fullWidth"
          text={
            state === 'published'
              ? t('admin.post.save')
              : t('admin.post.publish')
          }
          icon="next"
          showIconAfterText
          onClick={() => onSave('published')}
          disabled={saveDisabled}
          className={classes.button}
        />
      </div>
      {isSearchModalOpen && (
        <SearchModal
          onClose={() => setIsSearchModalOpen(false)}
          searchText={searchText}
          setSearchText={setSearchText}
          alreadySelectedProductIds={productIds}
          onProductChoose={onSearchProductChoose}
        />
      )}
      {isProductQueueModalOpen && (
        <ProductQueueModal
          queue={productQueue}
          onClose={() => {
            setIsProductQueueModalOpen(false)
          }}
          onUrlChoose={onProductUrlChoose}
        />
      )}
      {isDetectedProductsModalOpen && (
        <DetectedProductsModal
          groups={detectedProducts}
          onClose={() => {
            setIsDetectedProductsModalOpen(false)
          }}
          onProductChoose={onDetectedProductChoose}
        />
      )}
    </Page>
  )
)
